/**
 * ShipmentFormDialog - Criação/edição de remessa (Logística).
 *
 * Leiaute em seções no padrão do modal de Negócios (FormSection/FieldLabel):
 *  - Tipo: Envio ou Retorno (retorno parte de uma remessa de envio Entregue, via
 *    create_shipment_return — destinatário, vínculos e itens vêm da original).
 *  - Destinatário por tipo (Cliente, Fornecedor, Fabricante, Prestador): a lista mostra
 *    somente cadastros do tipo escolhido. O endereço é copiado do cadastro no servidor.
 *  - Itens já na criação (rascunho local, gravados após salvar via add_shipment_item).
 *    Prestador destinatário: itens já no estoque dele não aparecem (revalidado no servidor).
 *  - Transportadora com atalho "+" para cadastrar em Pessoas › Transportadoras.
 *
 * RELEASE: 2026-10-16 v2.3.0 - Seção Itens também na edição (ShipmentItemsPanel, gravação imediata).
 * RELEASE: 2026-10-16 v2.2.0 - Leiaute em coluna única (padrão do modal Novo negócio), dados
 *          sempre atualizados ao abrir (invalida cadastros/itens/remessas), seção
 *          Relacionamentos com nomes reais dos vínculos (get_shipment_details v1.2.0).
 * RELEASE: 2026-10-16 v2.1.0 - Destinatário em botões de opção, itens em catálogo modal,
 *          frete com máscara R$ e seção Vínculos (set_shipment_link) na criação/edição.
 * RELEASE: 2026-10-16 v2.0.0 - Leiaute por seções, tipo Envio/Retorno, tipo de destinatário,
 *          itens na criação e atalho de transportadora.
 * RELEASE: 2026-10-16 v1.0.0 - Versão inicial.
 */
import { canEditShipmentRecipient } from './shipmentStatus';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link2, Loader2, MapPin, Package, Plus, Repeat, Truck, User } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CurrencyInput } from '@/components/ui/currency-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { FormSection, FieldLabel } from '@/components/common/FormSection';
import { PersonSelector } from '@/components/persons/PersonSelector';
import { PersonFormDialog } from '@/components/persons/PersonFormDialog';
import { CustomerPicker } from '@/components/service-orders/ServiceOrderFormDialog';
import { UserSelector } from '@/components/crm/UserSelector';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import type { PersonRole } from '@/hooks/usePersons';
import {
  useShipmentMutations, useShipmentsList,
  type ShipmentDetails, type ShipmentEntityType,
} from '@/hooks/useShipments';
import { ShipmentItemsCatalogModal, type CatalogPick } from './ShipmentItemsCatalogModal';
import { ShipmentItemsSummary } from './ShipmentItemsSummary';
import { ShipmentItemsPanel } from './ShipmentItemsPanel';
import { addPicks } from './ShipmentItemsPanel';
import { ShipmentLinksPicker, type ShipmentLinkValue } from './ShipmentLinksPicker';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Remessa em edição (null = criação). */
  shipment: ShipmentDetails | null;
  /** Vínculo inicial (ex.: aberta a partir de uma O.S.). */
  link?: { entity_type: ShipmentEntityType; entity_id: string; label?: string | null } | null;
  /** Destinatário pré-selecionado (cliente). */
  defaultCustomer?: { id: string; name: string } | null;
  onSaved?: (id: string) => void;
}

/** Tipos de destinatário oferecidos (Cliente = crm_customers; demais = papéis de Pessoas). */
type RecipientKind = 'customer' | 'supplier' | 'manufacturer' | 'service_provider';
const RECIPIENT_KINDS: { value: RecipientKind; label: string; placeholder: string }[] = [
  { value: 'customer', label: 'Cliente', placeholder: 'Selecione o cliente' },
  { value: 'supplier', label: 'Fornecedor', placeholder: 'Selecione o fornecedor' },
  { value: 'manufacturer', label: 'Fabricante', placeholder: 'Selecione o fabricante' },
  { value: 'service_provider', label: 'Prestador', placeholder: 'Selecione o prestador' },
];

/** Item escolhido antes de salvar. */
type DraftItem = CatalogPick & { key: string };

interface FormState {
  direction: 'outbound' | 'return';
  returnOfId: string | null;
  recipientKind: RecipientKind;
  customerId: string | null;
  personId: string | null;
  carrierId: string | null;
  carrierService: string;
  trackingCode: string;
  freight: number;
  expected: string;
  responsibleId: string | null;
  notes: string;
}

export function ShipmentFormDialog({ open, onOpenChange, shipment, link, defaultCustomer, onSaved }: Props) {
  const { user } = useAuth();
  const { canCreate } = usePermissions();
  const qc = useQueryClient();

  // Dados frescos a cada abertura: nada de lista em cache (cadastros, itens, remessas, vínculos)
  useEffect(() => {
    if (!open) return;
    for (const key of ['persons', 'crm-customers-paginated', 'stock-manufacturers', 'shipments', 'shipment-candidates',
      'shipment-delivery-locations', 'global-search', 'service-orders']) {
      qc.invalidateQueries({ queryKey: [key] });
    }
  }, [open, qc]);
  const { upsert, addItem, createReturn, setLink } = useShipmentMutations();
  const isCreate = !shipment;
  const [labels, setLabels] = useState<{ customer: string | null; carrier: string | null }>({ customer: null, carrier: null });
  const [form, setForm] = useState<FormState>(() => emptyForm(null));
  const [items, setItems] = useState<DraftItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [carrierFormOpen, setCarrierFormOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [links, setLinks] = useState<ShipmentLinkValue[]>([]);

  // Remessas de envio entregues (origem de um retorno)
  const deliveredQuery = useShipmentsList(
    { search: '', status: 'delivered', carrierId: null, overdueOnly: false, page: 1, pageSize: 50 },
    open && isCreate && form.direction === 'return',
  );
  const deliveredOutbound = useMemo(
    () => (deliveredQuery.data?.items ?? []).filter((s) => s.direction === 'outbound'),
    [deliveredQuery.data],
  );

  // Recarrega o formulário a cada abertura
  useEffect(() => {
    if (!open) return;
    setItems([]);
    if (shipment) {
      // Vínculos atuais com nome/número vindo do servidor (fallback: código curto)
      setLinks(shipment.links.map((l) => ({ ...l, label: l.label || `#${l.entity_id.slice(0, 8).toUpperCase()}` })));
      setForm({
        direction: shipment.direction, returnOfId: shipment.return_of_id,
        recipientKind: shipment.recipient_customer_id ? 'customer' : shipment.recipient_is_provider ? 'service_provider'
          : (shipment.recipient_roles ?? []).includes('manufacturer') ? 'manufacturer' : 'supplier',
        customerId: shipment.recipient_customer_id, personId: shipment.recipient_person_id,
        carrierId: shipment.carrier_person_id, carrierService: shipment.carrier_service ?? '',
        trackingCode: shipment.tracking_code ?? '', freight: shipment.freight_cost ?? 0,
        expected: shipment.expected_delivery_date ?? '', responsibleId: shipment.responsible_user_id,
        notes: shipment.notes ?? '',
      });
      setLabels({ customer: shipment.recipient_name, carrier: shipment.carrier_name });
    } else {
      setForm({ ...emptyForm(user?.id ?? null), customerId: defaultCustomer?.id ?? null });
      setLinks(link ? [{ ...link, label: link.label || 'Registro de origem' }] : []);
      setLabels({ customer: defaultCustomer?.name ?? null, carrier: null });
    }
  }, [open, shipment, user?.id, defaultCustomer, link]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const isReturn = form.direction === 'return';
  // Destinatário editável na edição só em envio ainda não postado (antes de 'shipped'); retorno fica fixo
  const canEditRecipient = !isCreate && !!shipment && canEditShipmentRecipient(shipment.direction, shipment.status);
  const hasRecipient = form.recipientKind === 'customer' ? !!form.customerId : !!form.personId;
  const canSubmit = !isCreate || (isReturn ? !!form.returnOfId : hasRecipient);
  const providerRecipientId = form.recipientKind === 'service_provider' ? form.personId : null;
  const kindMeta = RECIPIENT_KINDS.find((k) => k.value === form.recipientKind)!;

  /** Troca o tipo de destinatário: limpa a seleção e os equipamentos (regra do prestador muda). */
  const changeRecipientKind = (v: RecipientKind) => {
    setForm((f) => ({ ...f, recipientKind: v, customerId: null, personId: null }));
    setLabels((l) => ({ ...l, customer: null }));
    setItems((list) => list.filter((i) => i.type === 'free' || i.type === 'product'));
  };

  /** Troca o prestador: equipamentos escolhidos podem estar com o novo prestador. */
  const changePerson = (id: string | null) => {
    set('personId', id);
    if (form.recipientKind === 'service_provider') setItems((list) => list.filter((i) => i.type === 'free' || i.type === 'product'));
  };

  /** Itens confirmados no catálogo entram no rascunho. */
  const addPicksDraft = (picks: CatalogPick[]) => {
    const stamp = Date.now();
    setItems((list) => [...list, ...picks.map((p, i) => ({ ...p, key: p.type === 'free' ? `free:${stamp}:${i}` : p.type === 'product' ? `product:${p.productId}:${stamp}:${i}` : `${p.type}:${p.itemId}` }))]);
    setCatalogOpen(false);
  };

  /** Aplica a diferença de vínculos (inclui novos e remove os retirados). */
  const syncLinks = async (id: string, before: { entity_type: ShipmentEntityType; entity_id: string }[]) => {
    const k = (l: { entity_type: string; entity_id: string }) => `${l.entity_type}:${l.entity_id}`;
    const now = new Set(links.map(k));
    const old = new Set(before.map(k));
    for (const l of links) if (!old.has(k(l))) await setLink.mutateAsync({ shipmentId: id, entityType: l.entity_type, entityId: l.entity_id, linked: true });
    for (const l of before) if (!now.has(k(l))) await setLink.mutateAsync({ shipmentId: id, entityType: l.entity_type, entityId: l.entity_id, linked: false });
  };

  const transportData = (): Record<string, unknown> => ({
    carrier_person_id: form.carrierId,
    carrier_service: form.carrierService.trim(),
    tracking_code: form.trackingCode.trim(),
    freight_cost: form.freight || null,
    expected_delivery_date: form.expected || null,
    responsible_user_id: form.responsibleId,
    notes: form.notes.trim(),
  });

  const submit = async () => {
    setSaving(true);
    try {
      // Edição: dados de transporte + destinatário (só envio ainda não postado; servidor revalida)
      if (!isCreate) {
        const data = transportData();
        if (canEditRecipient) {
          data.recipient_customer_id = form.recipientKind === 'customer' ? form.customerId : null;
          data.recipient_person_id = form.recipientKind !== 'customer' ? form.personId : null;
        }
        const id = await upsert.mutateAsync({ id: shipment.id, data });
        await syncLinks(id, shipment.links);
        onSaved?.(id);
        onOpenChange(false);
        return;
      }
      // Retorno: cria a partir da original e completa os dados de transporte
      if (isReturn) {
        const id = await createReturn.mutateAsync(form.returnOfId!);
        await upsert.mutateAsync({ id, data: transportData() });
        await syncLinks(id, []);
        onSaved?.(id);
        onOpenChange(false);
        return;
      }
      // Envio: cria e grava os itens escolhidos (cada um revalidado no servidor)
      const data = transportData();
      data.recipient_customer_id = form.recipientKind === 'customer' ? form.customerId : null;
      data.recipient_person_id = form.recipientKind !== 'customer' ? form.personId : null;
      const id = await upsert.mutateAsync({ id: null, data });
      await syncLinks(id, []);
      const failed = await addPicks(addItem, id, items);
      if (failed) toast.warning(`${failed} item(ns) não foram incluídos. Confira a aba Itens da remessa.`);
      onSaved?.(id);
      onOpenChange(false);
    } catch {
      // Erros já exibidos pelos avisos das mutações
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[100dvh] max-h-[100dvh] w-full max-w-full flex-col gap-0 p-0 sm:h-auto sm:max-h-[90vh] sm:max-w-3xl">
        <DialogHeader className="shrink-0 border-b px-6 py-4 text-left">
          <DialogTitle className="flex items-center gap-2"><Truck className="h-5 w-5 text-primary" />{isCreate ? 'Nova remessa' : `Editar ${shipment.number}`}</DialogTitle>
          <DialogDescription>O endereço de entrega é copiado do cadastro do destinatário.</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Coluna única: seções uma abaixo da outra (padrão do modal Novo negócio) */}
          <div className="space-y-6">
              <FormSection icon={Repeat} title="Tipo da remessa">
                {isCreate ? (
                  <ToggleGroup type="single" size="sm" value={form.direction} className="justify-start"
                    onValueChange={(v) => v && set('direction', v as FormState['direction'])}>
                    <ToggleGroupItem value="outbound">Envio</ToggleGroupItem>
                    <ToggleGroupItem value="return">Retorno</ToggleGroupItem>
                  </ToggleGroup>
                ) : (
                  <p className="text-sm">{shipment.direction === 'return' ? `Retorno de ${shipment.return_of_number ?? '—'}` : 'Envio'}</p>
                )}
                {isCreate && isReturn && (
                  <div className="space-y-1.5">
                    <FieldLabel required>Remessa original</FieldLabel>
                    <Select value={form.returnOfId ?? ''} onValueChange={(v) => set('returnOfId', v)}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder={deliveredQuery.isLoading ? 'Carregando...' : 'Selecione uma remessa entregue'} />
                      </SelectTrigger>
                      <SelectContent>
                        {deliveredOutbound.length === 0 && <p className="px-2 py-3 text-center text-xs text-muted-foreground">Nenhuma remessa de envio entregue.</p>}
                        {deliveredOutbound.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.number} · {s.recipient_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Destinatário, vínculos e itens são trazidos da remessa original. Ao entregar, os equipamentos voltam ao estoque padrão.</p>
                  </div>
                )}
              </FormSection>

              {!(isCreate && isReturn) && (
                <FormSection icon={User} title="Destinatário">
                  {isCreate || canEditRecipient ? (
                    <>
                      <div className="space-y-1.5">
                        <FieldLabel required>Tipo de destinatário</FieldLabel>
                        <RadioGroup value={form.recipientKind} onValueChange={(v) => changeRecipientKind(v as RecipientKind)}
                          className="flex flex-wrap gap-x-5 gap-y-2 pt-1">
                          {RECIPIENT_KINDS.map((k) => (
                            <label key={k.value} className="flex cursor-pointer items-center gap-2 text-sm">
                              <RadioGroupItem value={k.value} />{k.label}
                            </label>
                          ))}
                        </RadioGroup>
                      </div>
                      <div className="space-y-1.5">
                        <FieldLabel required>{kindMeta.label}</FieldLabel>
                        {form.recipientKind === 'customer' ? (
                          <CustomerPicker value={form.customerId} label={labels.customer} onSelect={(c) => {
                            set('customerId', c?.id ?? null);
                            setLabels((l) => ({ ...l, customer: c ? (c.nome || c.nome_fantasia || c.razao_social || 'Cliente') : null }));
                          }} />
                        ) : (
                          <PersonSelector key={form.recipientKind} role={form.recipientKind as PersonRole}
                            value={form.personId} onSelect={changePerson} placeholder={kindMeta.placeholder} />
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="rounded-md border bg-muted/40 p-3 text-sm">
                      <p className="font-medium">{shipment.recipient_name}</p>
                      <p className="flex items-center gap-1 text-xs text-muted-foreground"><MapPin className="h-3 w-3" />{formatAddress(shipment.address)}</p>
                    </div>
                  )}
                </FormSection>
              )}

              {isCreate && !isReturn && (
                <FormSection icon={Package} title="Itens" hint="(opcional)">
                  <ShipmentItemsSummary
                    editable
                    onAdd={() => setCatalogOpen(true)}
                    onRemove={(key) => setItems((list) => list.filter((x) => x.key !== key))}
                    rows={items.map((i) => (i.type === 'free' || i.type === 'product'
                      ? { key: i.key, type: i.type, label: i.type === 'free' ? i.description : i.label, place: null, serial: false, quantity: i.quantity }
                      : { key: i.key, type: i.type, label: i.label, place: i.place, serial: true, quantity: 1, status: i.status ?? null, components: i.components ?? null }))}
                  />
                </FormSection>
              )}

              {/* v2.3.0: na edição, itens gravados na hora pelo painel oficial (mesmas RPCs da aba Itens) */}
              {!isCreate && shipment && (
                <FormSection icon={Package} title="Itens">
                  <ShipmentItemsPanel shipment={shipment}
                    editable={shipment.direction === 'outbound' && ['draft', 'preparing', 'ready'].includes(shipment.status)} />
                </FormSection>
              )}

              <FormSection icon={Truck} title="Transporte">
                <div className="space-y-1.5">
                  <FieldLabel>Transportadora</FieldLabel>
                  <div className="flex gap-2">
                    <PersonSelector role="carrier" value={form.carrierId} selectedLabel={labels.carrier} className="min-w-0 flex-1"
                      onSelect={(id) => set('carrierId', id)} placeholder="Selecione a transportadora" noneLabel="Sem transportadora" />
                    {canCreate('persons_carriers') && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button type="button" variant="outline" size="icon" className="shrink-0" aria-label="Atalho: cadastrar nova transportadora"
                            onClick={() => setCarrierFormOpen(true)}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Atalho: cadastrar nova transportadora</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <FieldLabel>Serviço</FieldLabel>
                    <Input value={form.carrierService} maxLength={80} placeholder="Ex.: Sedex, PAC" onChange={(e) => set('carrierService', e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <FieldLabel>Código de rastreio</FieldLabel>
                    <Input value={form.trackingCode} maxLength={60} placeholder="Ex.: AA123456789BR"
                      onChange={(e) => set('trackingCode', e.target.value.replace(/[^A-Za-z0-9-]/g, '').toUpperCase())} />
                  </div>
                  <div className="space-y-1.5">
                    <FieldLabel>Previsão de entrega</FieldLabel>
                    <Input type="date" value={form.expected} onChange={(e) => set('expected', e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <FieldLabel>Frete (R$)</FieldLabel>
                    <CurrencyInput value={form.freight} onChange={(v) => set('freight', v)} />
                  </div>
                </div>
              </FormSection>

              <FormSection icon={User} title="Acompanhamento">
                <div className="space-y-1.5">
                  <FieldLabel>Responsável</FieldLabel>
                  <UserSelector value={form.responsibleId} onSelect={(id) => set('responsibleId', id)} allowClear={false} />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Observações</FieldLabel>
                  <Textarea value={form.notes} maxLength={2000} rows={3} onChange={(e) => set('notes', e.target.value)} />
                </div>
              </FormSection>

              <FormSection icon={Link2} title="Relacionamentos" hint="(opcional)">
                <ShipmentLinksPicker
                  value={links}
                  onAdd={(l) => setLinks((list) => [...list, l])}
                  onRemove={(l) => setLinks((list) => list.filter((x) => !(x.entity_type === l.entity_type && x.entity_id === l.entity_id)))}
                />
              </FormSection>
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t px-6 py-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving || !canSubmit}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salvar
          </Button>
        </DialogFooter>
      </DialogContent>

      <ShipmentItemsCatalogModal
        open={catalogOpen}
        onOpenChange={setCatalogOpen}
        onConfirm={addPicksDraft}
        excludeProviderId={providerRecipientId}
        hiddenIds={items.flatMap((i) => (i.type === 'free' || i.type === 'product' ? [] : [i.itemId]))}
        dealId={links.find((l) => l.entity_type === 'deal')?.entity_id ?? null}
      />

      {/* Atalho: nova transportadora já com o papel carrier; ao salvar fica selecionada */}
      <PersonFormDialog
        open={carrierFormOpen}
        onOpenChange={setCarrierFormOpen}
        person={null}
        defaultRole="carrier"
        onOpenExisting={() => setCarrierFormOpen(false)}
        onSuccess={(id, name) => {
          set('carrierId', id);
          setLabels((l) => ({ ...l, carrier: name }));
          setCarrierFormOpen(false);
        }}
      />
    </Dialog>
  );
}

function emptyForm(userId: string | null): FormState {
  return {
    direction: 'outbound', returnOfId: null, recipientKind: 'customer', customerId: null, personId: null,
    carrierId: null, carrierService: '', trackingCode: '', freight: 0, expected: '', responsibleId: userId, notes: '',
  };
}

/** Endereço em uma linha (snapshot da remessa). */
export function formatAddress(a: Record<string, string | null> | null | undefined): string {
  if (!a) return '—';
  const line = [a.street, a.number].filter(Boolean).join(', ');
  const city = [a.city, a.state].filter(Boolean).join('/');
  return [line, a.complement, a.district, city, a.cep].filter(Boolean).join(' · ') || 'Sem endereço no cadastro';
}
